import type { InstancePolicy, SettingsCatalogueEntry } from "@esi/shared";
import { describe, settingText } from "../admin/SettingsAdmin.tsx";
import { t as translate } from "../i18n/i18n.ts";
import type { ProvenanceInfo } from "../settings-shell/ProvenanceMark.tsx";
import { POLICY_EXTRAS } from "./policy-groups.ts";

/** The backoffice editors read a catalogue entry: the parts they use, from a policy. */
export const asEntry = (policy: InstancePolicy) =>
  ({ key: policy.key, input: policy.input }) as unknown as SettingsCatalogueEntry;

/** A policy's label and help: the console's own words, else the backoffice's. */
export function policyText(key: string): { label: string; help: string } {
  const copy = Object.hasOwn(POLICY_EXTRAS, key)
    ? POLICY_EXTRAS[key as keyof typeof POLICY_EXTRAS].copy
    : undefined;
  if (!copy) {
    return settingText(key);
  }
  return {
    label: translate(`console.policies.keys.${copy}.label`),
    help: translate(`console.policies.keys.${copy}.help`),
  };
}

/** A policy's value in words, as the backoffice words it. */
export function describePolicy(policy: InstancePolicy, value: unknown): string {
  return describe(asEntry(policy), value, []);
}

/** The instance holds a value in effect: a deployment lock wins over it. */
export const isSetForInstance = (policy: InstancePolicy) =>
  Object.hasOwn(policy, "value") && policy.deploymentLock === null;

/** Locked for every tenant (a deployment lock makes the operator's lock moot). */
export const isLockedForTenants = (policy: InstancePolicy) =>
  policy.locked && policy.deploymentLock === null;

/**
 * Where a policy's value comes from, as the API states it: set for this instance, locked for
 * every tenant, or fixed for this deployment. The ESI default has no mark.
 */
export function policyProvenance(policy: InstancePolicy): ProvenanceInfo[] {
  if (policy.deploymentLock !== null) {
    return [{ kind: "lockedAbove", label: translate("console.policies.marks.fixed") }];
  }
  const marks: ProvenanceInfo[] = [];
  if (isSetForInstance(policy)) {
    marks.push({ kind: "overridden", label: translate("console.policies.marks.setHere") });
  }
  if (isLockedForTenants(policy)) {
    marks.push({ kind: "locksBelow", label: translate("console.policies.lockedEverywhere") });
  }
  return marks;
}
